// RiskActionsTab — lists actions linked to the current risk.
// Data comes from GET /risks/{risk_id}/actions.
// Each row links to the action detail page.

import { useParams, useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useApi } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import { risksApi, type LinkedActionSummary } from '@/lib/api/risks'
import {
  actionLifecycleStateLabels,
  actionLifecycleStateColors,
} from '@/lib/api/actions'
import { queryKeys } from '@/lib/api/queryKeys'
import { ErrorState } from '@/components/shared/ErrorState'
import { TableSkeleton } from '@/components/shared/LoadingSkeleton'
import { Badge } from '@/components/ui/badge'
import { ListTodo, FolderOpen, User, Calendar, ExternalLink, AlertTriangle } from 'lucide-react'
import { cn } from '@/lib/utils'

export function RiskActionsTab() {
  const { slug } = useTenant()
  const { riskId } = useParams({ strict: false }) as { riskId: string }
  const navigate = useNavigate()
  const api = useApi()
  const riskApi = risksApi(api)

  const {
    data: actions,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: queryKeys.risks.actions(slug, riskId),
    queryFn: () => riskApi.actions(riskId),
  })

  if (isLoading) return <TableSkeleton rows={4} />

  if (error) {
    return (
      <div className="p-6">
        <ErrorState
          message="Failed to load linked actions"
          technicalDetail={error.message}
          onRetry={() => refetch()}
        />
      </div>
    )
  }

  if (!actions || actions.length === 0) {
    return (
      <div className="flex h-48 items-center justify-center text-muted-foreground">
        <div className="space-y-2 text-center">
          <ListTodo className="mx-auto h-10 w-10 text-muted-foreground/40" />
          <p className="text-sm">No actions are linked to this risk.</p>
          <p className="text-xs text-muted-foreground/60">
            Actions can be linked from the Actions section.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6">
      <div className="mb-3 text-sm text-muted-foreground">
        {actions.length} linked action{actions.length !== 1 ? 's' : ''}
      </div>
      <div className="divide-y rounded-lg border">
        {actions.map((action) => (
          <ActionRow
            key={action.link_id}
            action={action}
            onClick={() => navigate({ to: `/t/${slug}/actions/${action.action_id}` })}
          />
        ))}
      </div>
    </div>
  )
}

function ActionRow({
  action,
  onClick,
}: {
  action: LinkedActionSummary
  onClick: () => void
}) {
  return (
    <div
      className="group flex cursor-pointer items-center gap-4 px-4 py-3 transition-colors hover:bg-accent/30"
      onClick={onClick}
    >
      {/* Title + description */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium">{action.title}</span>
          {action.is_overdue && (
            <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 text-destructive" />
          )}
          <ExternalLink className="h-3 w-3 flex-shrink-0 text-muted-foreground opacity-0 group-hover:opacity-100" />
        </div>
        {action.description && (
          <div className="mt-0.5 truncate text-xs text-muted-foreground">
            {action.description}
          </div>
        )}
      </div>

      {/* Category */}
      <div className="hidden items-center gap-1 text-xs text-muted-foreground md:flex">
        <FolderOpen className="h-3 w-3" />
        <span className="max-w-[120px] truncate">{action.category_name}</span>
      </div>

      {/* Owner */}
      {action.owner_display_name && (
        <div className="hidden items-center gap-1 text-xs text-muted-foreground lg:flex">
          <User className="h-3 w-3" />
          <span className="max-w-[100px] truncate">{action.owner_display_name}</span>
        </div>
      )}

      {/* Due date */}
      {action.due_date && (
        <div
          className={cn(
            'hidden items-center gap-1 text-xs xl:flex',
            action.is_overdue ? 'text-destructive' : 'text-muted-foreground',
          )}
        >
          <Calendar className="h-3 w-3" />
          <span>{new Date(action.due_date).toLocaleDateString()}</span>
        </div>
      )}

      {/* Status badge */}
      <Badge
        variant="outline"
        className={cn(
          'flex-shrink-0 px-1.5 py-0 text-xs',
          actionLifecycleStateColors[action.lifecycle_state] ?? 'bg-gray-100 text-gray-700',
        )}
      >
        {actionLifecycleStateLabels[action.lifecycle_state] ?? action.lifecycle_state}
      </Badge>
    </div>
  )
}
